import React, { useState, useEffect } from 'react';

import { Grid } from '@mui/material';

import CustomDialog from 'components/shared/CustomDialog';

import { arrayValidation } from 'utils/array';
import { sleep } from 'utils/async';

import type { Column, DataConfigTable } from 'api/shared/models';

import MyColumns from './MyColumns';
import DialogHeader from './DialogHeader';
import AvailableColumns from './AvailableColumns';

import { getColumnsByConfiguration } from './helpers';

import { styles } from './styles';

interface Props {
  open: boolean;
  dataColumns: DataConfigTable;
  handleClose: () => void;
  handleUpdateSortColumns: (newColumns: Column[]) => void;
}

const ConfigurationSortModal = ({
  open,
  handleClose,
  dataColumns,
  handleUpdateSortColumns,
}: Props) => {
  const [availableColumns, setAvailableColumns] = useState<Column[]>([]);
  const [myColumns, setMyColumns] = useState<Column[]>([]);

  const moveRows = (rowIndex: number, newIndex: number) => {
    const newMyColumns = [...myColumns];
    const dragColumn = myColumns[rowIndex];

    newMyColumns.splice(rowIndex, 1);
    newMyColumns.splice(newIndex, 0, dragColumn);
    setMyColumns(newMyColumns);
  };

  const handleSort = (column: Column) => {
    setMyColumns(
      myColumns.map((myColumn) => {
        const direction = column.direction === 'asc' ? 'desc' : 'asc';
        if (myColumn.apiField === column.apiField) {
          return {
            ...myColumn,
            direction,
          };
        }
        return myColumn;
      }),
    );
  };

  const startProcessByColumns = (columns: Column[], sortColumns: Column[]) => {
    const { newAvailableColumns, newMyColumns } = getColumnsByConfiguration(
      columns,
      sortColumns,
    );
    setMyColumns(newMyColumns);
    setAvailableColumns(newAvailableColumns);
  };

  const handleChangeMyColumns = async (column: Column) => {
    await sleep(100);
    const newAvailableColumns = [
      ...availableColumns,
      { ...column, status: false, direction: undefined },
    ];
    setAvailableColumns(newAvailableColumns);

    if (arrayValidation(myColumns)) {
      const newMyColumns = myColumns.filter(
        (item) => item.apiField !== column.apiField,
      );
      setMyColumns(newMyColumns);
    }
  };

  const handleChangeAvailableColumns = async (column: Column) => {
    await sleep(100);
    const newColumn = availableColumns.find(
      (item) => item.apiField === column.apiField,
    );
    if (newColumn) {
      setMyColumns([
        ...myColumns,
        { ...newColumn, status: true, direction: 'asc' },
      ]);
    }

    if (arrayValidation(availableColumns)) {
      const newAvailableColumns = availableColumns.filter(
        (item) => item.apiField !== column.apiField,
      );
      setAvailableColumns(newAvailableColumns);
    }
  };

  const handleSubmit = () => {
    handleUpdateSortColumns(myColumns);
  };

  useEffect(() => {
    startProcessByColumns(
      dataColumns?.columns || [],
      dataColumns?.sortColumns || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (
    <>
      <Grid container>
        <DialogHeader handleSubmit={handleSubmit} />
        <Grid item xs={12} style={{ margin: 24 }}>
          <Grid container spacing={6}>
            <Grid item xs={6} md={6} lg={6}>
              <AvailableColumns
                columns={availableColumns}
                onClick={handleChangeAvailableColumns}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <MyColumns
                columns={myColumns}
                onClick={handleChangeMyColumns}
                moveRows={moveRows}
                handleSort={handleSort}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      body={body}
      fullWidth={true}
      sx={styles.dialog}
    />
  );
};

export default ConfigurationSortModal;
